import { useState, useEffect, useRef, useCallback, MutableRefObject } from 'react';
import { Linkedin, Loader2, AlertCircle, TestTube, UserCog, ScrollText, Volume2 } from 'lucide-react';
import { ModernScriptCard } from './ModernScriptCard';
import { FloatingSaveBar } from './FloatingSaveBar';
import TestAgentModal from '../CallAgent/TestAgentModal';
import { AgentRulesSection } from './AgentRulesSection';
import MultiSelectDropdown from '../shared/MultiSelectDropdown';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import { getAgentSettings, saveAgentIdentityConfig, getLinkedInAccounts, generateAgentScript, getUserProfile, getAgentSettingsRow, triggerReplyPromptGeneration } from '../../services/settingsService';
import { AgentAvatarUpload } from './AgentAvatarUpload';
import type { LinkedInAgentSettings as LinkedInAgentSettingsType, LinkedInConnection, LinkedInTemplateDraft, LinkedInTemplateKey } from '../../types/settings';
import { LI_TEMPLATE_DEFINITIONS } from '../../types/settings';
import { getLinkedInDrafts, getLinkedInTemplateStatuses, approveSingleLinkedInTemplate, saveLinkedInTemplatesAsDrafts } from '../../services/templateService';
import type { TemplateStatus } from '../../services/templateService';
import { DraftBanner } from './DraftBanner';
import { TEMPLATE_VARIABLES } from '../../hooks/useVariableInsertion';
import { TONE_OPTIONS } from '../../shared/callAgentConstants';
import MessagingStyleTooltip from '../shared/MessagingStyleTooltip';
import { useAuth } from '../../contexts/AuthContext';
import { canEditAgentSettings } from '../../utils/permissions';
import { supabase } from '../../lib/supabase';

interface LinkedInAgentSettingsProps {
  onDirtyStateChange?: (dirty: boolean) => void;
  saveRef?: MutableRefObject<(() => Promise<void>) | null>;
  discardRef?: MutableRefObject<(() => void) | null>;
}

export function LinkedInAgentSettings({ onDirtyStateChange, saveRef, discardRef }: LinkedInAgentSettingsProps) {
  const { user } = useAuth();
  const canEdit = canEditAgentSettings(user);
  const [settings, setSettings] = useState<LinkedInAgentSettingsType | null>(null);
  const [originalSettings, setOriginalSettings] = useState<LinkedInAgentSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | undefined>();
  const [linkedInAccounts, setLinkedInAccounts] = useState<LinkedInConnection[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [showTestModal, setShowTestModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const dirtyCardsRef = useRef(new Set<string>());
  const [drafts, setDrafts] = useState<LinkedInTemplateDraft[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [templateStatuses, setTemplateStatuses] = useState<Record<string, TemplateStatus>>({});
  const [approvingKey, setApprovingKey] = useState<string | null>(null);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(null);
  const [isPlayingVoiceNote, setIsPlayingVoiceNote] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const init = async () => {
      await loadSettings();
      loadDrafts();
    };
    init();
    loadLinkedInAccounts();
    getAgentSettingsRow('linkedin').then(row => {
      if (row?.avatar_url) setAgentAvatarUrl(row.avatar_url);
    });
  }, []);

  const handleCardDirtyChange = useCallback((cardKey: string, isDirty: boolean) => {
    if (isDirty) {
      dirtyCardsRef.current.add(cardKey);
    } else {
      dirtyCardsRef.current.delete(cardKey);
    }
    setHasLocalEdits(dirtyCardsRef.current.size > 0);
  }, []);

  useEffect(() => {
    if (settings && originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings) || hasLocalEdits;
      setHasChanges(changed);
      onDirtyStateChange?.(changed);
    }
  }, [settings, originalSettings, hasLocalEdits]);

  useEffect(() => {
    return () => { onDirtyStateChange?.(false); };
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const data = await getAgentSettings('linkedin');
    setSettings(data);
    setOriginalSettings(JSON.parse(JSON.stringify(data)));
    setLoading(false);
  };

  const loadLinkedInAccounts = async () => {
    setAccountsLoading(true);
    const accounts = await getLinkedInAccounts();
    setLinkedInAccounts(accounts);
    setAccountsLoading(false);
  };

  const voiceNoteHydratedRef = useRef(false);
  useEffect(() => {
    if (voiceNoteHydratedRef.current) return;
    if (!settings?.linkedin_account_id || linkedInAccounts.length === 0) return;
    const account = linkedInAccounts.find(a => a.id === settings.linkedin_account_id);
    if (!account) return;
    voiceNoteHydratedRef.current = true;
    const needsUpdate =
      (!settings.voice_note_example && account.voice_note_example) ||
      (settings.voice_notes_allowed !== (account.voice_notes_allowed ?? false)) ||
      (!settings.location && account.country);
    if (needsUpdate) {
      const updated = {
        ...settings,
        voice_notes_allowed: account.voice_notes_allowed ?? settings.voice_notes_allowed ?? false,
        voice_note_example: account.voice_note_example || settings.voice_note_example || '',
        location: settings.location || account.country || '',
      };
      setSettings(updated);
      setOriginalSettings(JSON.parse(JSON.stringify(updated)));
    }
  }, [settings?.linkedin_account_id, linkedInAccounts]);

  const handleToggleVoiceNotes = async (enabled: boolean) => {
    if (!settings?.linkedin_account_id) return;
    setSettings({ ...settings, voice_notes_allowed: enabled });
    await supabase
      .from('linkedin_accounts')
      .update({ voice_notes_allowed: enabled })
      .eq('id', settings.linkedin_account_id);
  };

  const handlePlayVoiceNote = () => {
    if (!settings?.voice_note_example) return;
    if (isPlayingVoiceNote && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingVoiceNote(false);
      return;
    }
    const audio = new Audio(settings.voice_note_example);
    audioRef.current = audio;
    audio.onended = () => setIsPlayingVoiceNote(false);
    audio.play();
    setIsPlayingVoiceNote(true);
  };

  const loadDrafts = async () => {
    setDraftsLoading(true);
    const liDrafts = await getLinkedInDrafts();
    const statuses = await getLinkedInTemplateStatuses();
    setTemplateStatuses(statuses);

    if (liDrafts.length > 0) {
      const hasFreshFromGeneration = liDrafts.some(d => d.source === 'sales_materials');

      if (hasFreshFromGeneration) {
        setDrafts(liDrafts);
      } else {
        setSettings(prev => {
          if (!prev) return prev;
          const newTemplates = { ...prev.templates };
          for (const draft of liDrafts) {
            const key = draft.template_key as LinkedInTemplateKey;
            if (key && newTemplates[key] && statuses[key] === 'draft') {
              newTemplates[key] = { text: draft.message_text };
            }
          }
          const updated = { ...prev, mode: 'template' as const, templates: newTemplates as LinkedInAgentSettingsType['templates'] };
          setOriginalSettings(JSON.parse(JSON.stringify(updated)));
          return updated;
        });
        setDrafts([]);
      }
    } else {
      setDrafts([]);
    }
    setDraftsLoading(false);
  };

  const applyDrafts = () => {
    if (!settings || drafts.length === 0) return;

    const legacyToKey: Record<string, LinkedInTemplateKey> = {
      connection_request: 'li_outreach_connection',
      first_message: 'li_outreach_intro',
      followup_1: 'li_outreach_fu_1',
      followup_2: 'li_outreach_fu_2',
      pitch: 'li_outreach_pitch',
      followup_3: 'li_outreach_fu_3',
    };

    const newTemplates = { ...settings.templates };
    const newStatuses: Record<string, TemplateStatus> = { ...templateStatuses };
    for (const draft of drafts) {
      const key = (legacyToKey[draft.step_name] || draft.template_key) as LinkedInTemplateKey;
      if (key && newTemplates[key]) {
        newTemplates[key] = { text: draft.message_text };
        newStatuses[key] = 'draft';
      }
    }

    setTemplateStatuses(newStatuses);
    setSettings({
      ...settings,
      mode: 'template',
      templates: newTemplates as LinkedInAgentSettingsType['templates'],
    });
    setDrafts([]);
  };

  const handleUpdateTemplate = (key: LinkedInTemplateKey, text: string) => {
    if (!settings) return;
    setSettings({
      ...settings,
      templates: {
        ...settings.templates!,
        [key]: { text },
      },
    });
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    const success = await saveAgentIdentityConfig('linkedin', settings);

    if (success) {
      const hasDraftTemplates = Object.values(templateStatuses).some(s => s === 'draft');
      if (hasDraftTemplates && settings.templates) {
        const templatesMap: Record<string, { text: string }> = {};
        for (const [key, val] of Object.entries(settings.templates)) {
          if (val && typeof val === 'object' && 'text' in val) {
            templatesMap[key] = { text: (val as { text: string }).text };
          }
        }
        await saveLinkedInTemplatesAsDrafts(templatesMap, templateStatuses);
      }

      setSaved(true);
      setLastSaved(new Date());
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      setHasChanges(false);
      triggerReplyPromptGeneration('linkedin');
      setTimeout(() => setSaved(false), 2000);
    }

    setSaving(false);
  };

  const handleApproveTemplate = async (key: LinkedInTemplateKey, messageText: string) => {
    setApprovingKey(key);
    const success = await approveSingleLinkedInTemplate(key, messageText);
    if (success) {
      setTemplateStatuses(prev => ({ ...prev, [key]: 'approved' }));
    }
    setApprovingKey(null);
  };

  const handleDiscard = () => {
    if (originalSettings) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
      setHasChanges(false);
      onDirtyStateChange?.(false);
    }
  };

  useEffect(() => {
    if (saveRef) saveRef.current = handleSave;
    if (discardRef) discardRef.current = handleDiscard;
  });


  const handleGenerateMessage = async (section: string, tone: string, length: string, currentContent?: string): Promise<string> => {
    try {
      const profile = await getUserProfile();
      const response = await generateAgentScript({
        agent_type: 'linkedin',
        section: section,
        company_name: profile?.company_name || undefined,
        tone,
        length,
        user_id: user?.email,
        client_id: user?.client_id,
        current_content: currentContent || undefined,
      });

      if (response?.generated_script) {
        return response.generated_script;
      }
      throw new Error('No script generated');
    } catch (error) {
      console.error('Error generating LinkedIn message:', error);
      throw error;
    }
  };

  const configuredCount = LI_TEMPLATE_DEFINITIONS.filter(
    tmpl => templateStatuses[tmpl.key] === 'approved'
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load settings</p>
      </div>
    );
  }

  return (
    <div className={`max-w-full ${hasChanges || saving || saved ? 'pb-28' : ''}`}>
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <AgentAvatarUpload
            agentType="linkedin"
            avatarUrl={agentAvatarUrl}
            onAvatarChange={setAgentAvatarUrl}
          />
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display mb-2">LinkedIn Agent</h2>
            <p className="text-gray-600">
              Configure your AI LinkedIn agent's messages and behavior
            </p>
          </div>
        </div>
        {canEdit && (
          <button
            onClick={() => setShowTestModal(true)}
            className="px-4 py-2.5 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 font-semibold"
          >
            <TestTube className="w-5 h-5" />
            Test Agent
          </button>
        )}
      </div>

      {!canEdit && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">View Only</p>
            <p className="text-xs text-blue-800">
              Only admins can edit agent settings. Contact your admin to make changes.
            </p>
          </div>
        </div>
      )}

      {linkedInAccounts.length === 0 && !accountsLoading && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-yellow-900 mb-1">
              LinkedIn Not Connected
            </p>
            <p className="text-xs text-yellow-800">
              Connect your LinkedIn account in the Connections section to enable the LinkedIn agent.
            </p>
          </div>
        </div>
      )}

      <DraftBanner
        draftCount={drafts.length}
        agentType="LinkedIn"
        loading={draftsLoading}
        onApplyDrafts={applyDrafts}
        onDismissDrafts={() => setDrafts([])}
      />

      <div className={`space-y-6 ${!canEdit ? 'pointer-events-none opacity-75' : ''}`}>
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <UserCog className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Agent Identity & Behavior</h3>
              <p className="text-sm text-gray-500">Configure your LinkedIn agent's profile and preferences</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Agent Account</label>
                {accountsLoading ? (
                  <div className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading accounts...
                  </div>
                ) : linkedInAccounts.length === 0 ? (
                  <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-400 bg-gray-50">
                    No accounts connected
                  </div>
                ) : (
                  <SimpleCustomSelect
                    value={settings.linkedin_account_id || ''}
                    onChange={(value) => {
                      const account = linkedInAccounts.find(a => a.id === value);
                      if (account) {
                        setSettings({
                          ...settings,
                          linkedin_account_id: account.id,
                          location: account.country || '',
                          voice_notes_allowed: account.voice_notes_allowed ?? false,
                          voice_note_example: account.voice_note_example || '',
                        });
                      }
                    }}
                    options={linkedInAccounts.map(account => ({
                      value: account.id,
                      label: account.linkedin_profile_name || account.linkedin_profile_url || 'LinkedIn Account',
                    }))}
                    placeholder="Select LinkedIn Profile"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 min-h-[42px] flex items-center">
                  {settings.location ? settings.location.charAt(0).toUpperCase() + settings.location.slice(1) : ''}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
                <MultiSelectDropdown
                  value={settings.languages || ['english']}
                  onChange={(values) => setSettings({ ...settings, languages: values.includes('english') ? values : ['english', ...values] })}
                  options={[
                    { value: 'english', label: 'English' },
                    { value: 'french', label: 'French' },
                    { value: 'spanish', label: 'Spanish' },
                    { value: 'italian', label: 'Italian' },
                    { value: 'arabic', label: 'Arabic' },
                    { value: 'russian', label: 'Russian' },
                  ]}
                  placeholder="Select languages"
                  pinnedValues={['english']}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Voice Notes</label>
                <div className="flex items-center gap-3 min-h-[42px]">
                  <button
                    type="button"
                    onClick={() => handleToggleVoiceNotes(!settings.voice_notes_allowed)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#1875FF]/50 focus:ring-offset-2 ${
                      settings.voice_notes_allowed
                        ? 'bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                        : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                        settings.voice_notes_allowed ? 'translate-x-[22px]' : 'translate-x-[2px]'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-600">
                    {settings.voice_notes_allowed ? 'Enabled' : 'Disabled'}
                  </span>
                  {settings.voice_notes_allowed && settings.voice_note_example && (
                    <button
                      type="button"
                      onClick={handlePlayVoiceNote}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        isPlayingVoiceNote
                          ? 'bg-[#1875FF]/15 text-[#1875FF] animate-pulse'
                          : 'bg-gray-100 text-gray-500 hover:bg-[#1875FF]/10 hover:text-[#1875FF]'
                      }`}
                      title={isPlayingVoiceNote ? 'Stop playing' : 'Play voice note example'}
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Messaging Style
                  <MessagingStyleTooltip agentType="linkedin" />
                </label>
                <SimpleCustomSelect
                  value={settings.tone || 'friendly_casual'}
                  onChange={(value) => setSettings({ ...settings, tone: value })}
                  options={TONE_OPTIONS}
                  placeholder="Select style"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <input
                  type="text"
                  value={settings.role || ''}
                  onChange={(e) => setSettings({ ...settings, role: e.target.value })}
                  placeholder="Sales Consultant"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Objective</label>
                <input
                  type="text"
                  value={settings.objective || ''}
                  onChange={(e) => setSettings({ ...settings, objective: e.target.value })}
                  placeholder="Book a meeting"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Follow Ups</label>
                <input
                  type="number"
                  value={settings.max_messages}
                  onChange={(e) => {
                    const val = Math.min(Math.max(parseInt(e.target.value) || 4, 2), 5);
                    setSettings({ ...settings, max_messages: val });
                  }}
                  min="2"
                  max="5"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm transition-all"
                />
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className={`mt-6 space-y-6 ${!canEdit ? 'opacity-75' : ''}`}>
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <ScrollText className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Outreach Templates</h3>
              <p className="text-sm text-gray-500">Manage the outreach sequence your agent sends on LinkedIn</p>
            </div>
            <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 border border-[#1875FF]/15">
              <span className="text-xs font-bold text-[#1875FF]">{configuredCount}</span>
              <span className="text-xs text-gray-400">/</span>
              <span className="text-xs font-medium text-gray-500">{LI_TEMPLATE_DEFINITIONS.length}</span>
            </div>
          </div>

          <div className="space-y-4">
            {LI_TEMPLATE_DEFINITIONS.map((tmpl) => (
              <ModernScriptCard
                key={tmpl.key}
                title={`LI #${tmpl.number}: ${tmpl.label}`}
                content={settings.templates?.[tmpl.key]?.text || ''}
                placeholder={tmpl.placeholder}
                onUpdate={(text) => handleUpdateTemplate(tmpl.key, text)}
                onGenerateAI={(tone, length, currentContent) => handleGenerateMessage(tmpl.key, tone, length, currentContent)}
                onDirtyChange={(isDirty) => handleCardDirtyChange(tmpl.key, isDirty)}
                maxLength={tmpl.key === 'li_outreach_connection' ? 300 : 1000}
                icon={<Linkedin className="w-5 h-5 text-[#1875FF]" />}
                variables={TEMPLATE_VARIABLES}
                templateStatus={templateStatuses[tmpl.key]}
                onApproveTemplate={async (text) => {
                  await handleApproveTemplate(tmpl.key, text);
                }}
                approvingTemplate={approvingKey === tmpl.key}
                readOnly={!canEdit}
              />
            ))}
          </div>
        </div>

        <div className={!canEdit ? 'pointer-events-none' : ''}>
          <AgentRulesSection
            doRules={settings.do_rules}
            dontRules={settings.dont_rules}
            onDoRulesChange={(rules) => setSettings({ ...settings, do_rules: rules })}
            onDontRulesChange={(rules) => setSettings({ ...settings, dont_rules: rules })}
            subtitle="Define what your agent should and shouldn't do on LinkedIn"
          />
        </div>
      </div>

      {canEdit && (
        <FloatingSaveBar
          hasChanges={hasChanges}
          saving={saving}
          saved={saved}
          lastSaved={lastSaved}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      )}

      <TestAgentModal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        agentType="linkedin"
      />
    </div>
  );
}
